import { Alert, Box, Skeleton } from '@mui/material';
import { Fragment, useEffect, useMemo } from 'react';
import { EspacioPublicitario } from '../../../shared/ads/EspacioPublicitario.js';
import { intercaladosEntreBloques } from '../../../shared/ads/cadencia.js';
import { TiraLateral } from '../../../shared/lateral/TiraLateral.js';
import { SECCION_KIND } from '../../../seccion/models/entities/SeccionData.js';
import { useObservable } from '../../../shared/hooks/useObservable.js';
import { useViewModel } from '../../../shared/hooks/useViewModel.js';
import { useSincronizarPagina } from '../../../shared/shell/paginaPublica.js';
import { useSenalDePagina } from '../../../shared/senales/useSenalDePagina.js';
import { useHead } from '../../../shared/seo/useHead.js';
import { useSitio, useSsrData } from '../../../shared/ssr/SsrDataProvider.js';
import { headDeHome } from '../../home.head.js';
import { HOME_ROUTE_KEY } from '../../home.loader.js';
import type { HomeData } from '../../models/entities/HomeData.js';
import { HomeViewModel } from '../../viewmodels/HomeViewModel.js';
import { Bloque } from '../components/Bloque.js';
import { Portada } from '../components/Portada.js';
import { tieneFrentePropio, tieneRestoBajoElFrente } from '../components/portadaPartes.js';
import { asignarPresentacion, ordenDelCuerpo } from '../components/rotacion.js';

/**
 * La home: **una pila de bloques variable en cantidad y orden**. Esta vista no conoce
 * nombres de categorías ni posiciones reservadas — itera lo que el server resolvió, le
 * asigna plantilla y ground por rotación, e intercala la publicidad por cadencia. Con 5,
 * 7 o 9 bloques la lógica es exactamente la misma.
 *
 * En escritorio (≥1200) el cuerpo convive con la columna lateral: la tira única de Última
 * hora con robapáginas cada 5 notas, cortada a la altura del cuerpo. Por debajo, el mismo
 * bloque se dibuja en el flujo como lista y la columna desaparece.
 *
 * Las maquetas B y C de la portada son el mismo mecanismo al revés: su **frente** se dibuja a ancho
 * completo por encima de las dos columnas, así que Última hora arranca debajo de la nota única (B)
 * o de las dos grandes (C) en vez de acompañarlas desde arriba. Dos renders, uno por corte, y
 * `display` decide cuál se ve —cero JavaScript de layout—. Por debajo de 1200 no aplica: sin
 * lateral no hay nada que esquivar y el bloque se arma entero en el flujo, como cualquier otro.
 */
export function HomeView() {
  const initial = useSsrData<HomeData>(HOME_ROUTE_KEY);
  const sitio = useSitio();
  const vm = useViewModel(() => new HomeViewModel(initial));
  const state = useObservable(vm.state$);

  useEffect(() => {
    void vm.load(); // no-op si el estado vino del server
  }, [vm]);
  useSincronizarPagina(state.data);
  useSenalDePagina('PORTADA', state.data ? 'portada' : null);
  // Volviendo a la portada desde una nota no corre ningún loader: sin esto la pestaña
  // seguiría mostrando el título de la nota anterior.
  useHead(useMemo(() => headDeHome(sitio), [sitio]));

  const data = state.data;
  if (!data) {
    return (
      <Box sx={{ py: 2 }}>
        {state.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : (
          <>
            <Skeleton variant="rectangular" height={220} />
            <Skeleton height={48} sx={{ mt: 2 }} />
            <Skeleton height={48} width="70%" />
          </>
        )}
      </Box>
    );
  }

  // Última hora se apila **al final** del cuerpo, no donde la manda el servidor (ver
  // `ordenDelCuerpo`). Las plantillas y los grounds se asignan sobre ese orden, que es el que el
  // lector ve.
  const presentaciones = asignarPresentacion(ordenDelCuerpo(data.bloques));
  const cuerpo = presentaciones.filter((p) => p.bloque.kind !== 'ULTIMA_HORA');
  const presentacionUltimaHora = presentaciones.find((p) => p.bloque.kind === 'ULTIMA_HORA');
  // La cadencia se cuenta sobre el cuerpo **sin** Última hora: en escritorio ese bloque no se
  // dibuja, así que si contara, un aviso podría quedar último de la página con nada abajo —
  // justo lo que la regla de `intercaladosEntreBloques` evita.
  const conIntercalado = intercaladosEntreBloques(cuerpo.map((p) => p.bloque));
  const ultimaHora = data.bloques.find((b) => b.kind === 'ULTIMA_HORA');
  // Puede no venir: una portada sin una sola nota no se emite (handoff §5).
  const portada = data.bloques.find((b) => b.kind === 'PORTADA');
  const maquetaPortada = portada?.kind === 'PORTADA' ? portada.maqueta : null;
  const frenteAparte = maquetaPortada !== null && tieneFrentePropio(maquetaPortada);

  return (
    <>
      {/* El h1 de la portada es el nombre del medio; visualmente la marca ya está en la
          cabecera, así que va solo para el lector de pantalla y el crawler.
          Las medidas van en px explícitos: en `sx`, un número entre 0 y 1 en width/height
          es un porcentaje, así que `width: 1` sería 100% y el h1 absoluto sobresaldría por
          la derecha — el clipPath lo esconde a la vista, pero deja scroll horizontal. */}
      <Box
        component="h1"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          margin: '-1px',
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        {data.shell.tenant.nombre} — noticias
      </Box>
      <EspacioPublicitario ubicacion="HEADER" ad={data.ads.HEADER} />

      {/* El frente de B y C, fuera del contenedor de dos columnas y por encima de él: es lo que
          hace que Última hora arranque debajo y no al costado. Solo en ≥1200, el único corte con
          columna lateral; más abajo lo dibuja el bloque del cuerpo, entero. */}
      {frenteAparte && portada?.kind === 'PORTADA' && (
        <Box sx={{ display: { xs: 'none', lg: 'block' }, mt: 1 }}>
          <Portada
            slots={portada.slots}
            maqueta={portada.maqueta}
            parte="frente"
            adPieDePortada={undefined}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' },
          gap: { xs: 0, lg: 4 },
          alignItems: 'stretch',
          mt: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {cuerpo.map((presentacion, i) => (
            <Fragment key={presentacion.bloque.id}>
              {presentacion.bloque.kind === 'PORTADA' && frenteAparte && maquetaPortada ? (
                <>
                  {/* Sin lateral, el bloque va entero en el flujo. */}
                  <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                    <Bloque presentacion={presentacion} ads={data.ads} partePortada="completa" />
                  </Box>
                  {/* Con lateral, el frente ya se dibujó arriba y acá queda lo de abajo. En B no
                      queda nada, y no se dibuja ni el envoltorio: sería una franja de aire donde
                      tendría que arrancar Última hora. */}
                  {tieneRestoBajoElFrente(maquetaPortada) && (
                    <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                      <Bloque presentacion={presentacion} ads={data.ads} partePortada="resto" />
                    </Box>
                  )}
                </>
              ) : (
                <Bloque presentacion={presentacion} ads={data.ads} />
              )}
              {conIntercalado.has(i) && (
                <Box sx={{ py: 2 }}>
                  <EspacioPublicitario ubicacion="IN_FEED" ad={data.ads.IN_FEED} />
                </Box>
              )}
            </Fragment>
          ))}

          {/* Última hora cierra el cuerpo, y solo abajo de 1200: en escritorio es la columna
              lateral de al lado, que arranca arriba de todo y trae la lista larga entera. */}
          {presentacionUltimaHora && (
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
              <Bloque presentacion={presentacionUltimaHora} ads={data.ads} />
            </Box>
          )}
        </Box>

        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <TiraLateral
            titulo={data.shell.ultimaHoraNombre}
            notas={(ultimaHora?.kind === 'ULTIMA_HORA' ? ultimaHora.notas : []).map((n) => ({
              id: n.id,
              title: n.title,
              path: n.path,
              publishedAt: n.publishedAt,
            }))}
            verTodo={`/${SECCION_KIND.ultimaHora}`}
            ad={data.ads.SIDEBAR}
          />
        </Box>
      </Box>
    </>
  );
}
